/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import React, { useEffect, useState } from "react";
import { compose, AnyAction } from "redux";
import { connect } from "react-redux";
import { Location } from "history";
import { navigate } from "utils/routes.utils";
import { parse } from "query-string";
import { BILLING_PATH } from "pages/billing/routes";
import styles from "./billing.module.css";
import { ThunkDispatch } from "redux-thunk";
import wrapPage from "containers/page/page";
import { t } from "i18n";
import { showUnauthorizedPage } from "store/error-page";
import ContentHeader from "pages/settings/components/content-header/content-header";
import MonthPicker from "pages/billing/components/month-picker/month-picker";
import { fetchBandwidthReport, PartialAppState } from "store/billing/billing";

interface BillingStateProps {
  title: string;
  canAccessBilling?: boolean;
  content: string;
  currentTime: Date;
}
interface BillingDispatchProps {
  showUnauthorizedPage: () => void;
  loadBandwidthReport: (duration: string) => void;
}

interface OwnProps {
  location?: Location;
}

type BillingProps = BillingStateProps & BillingDispatchProps & OwnProps;

const Billing: React.FC<BillingProps> = ({
  showUnauthorizedPage,
  canAccessBilling,
  loadBandwidthReport,
  content,
  location,
  currentTime
}) => {
  let maxYear, year, maxMonth, month;
  maxYear = year = currentTime.getFullYear();
  maxMonth = month = currentTime.getMonth() + 1;

  const queryMonth = location && (parse(location.search)["duration"] as string);
  if (queryMonth) {
    year = parseInt(queryMonth.split("-")[0]);
    month = parseInt(queryMonth.split("-")[1]);
  }

  const [duration, setDuration] = useState({ year, month });

  useEffect(() => {
    const fetchData = async () => {
      const monthParam = duration.month.toString().length === 1 ? "0" + duration.month : duration.month;
      const param = `${duration.year}-${monthParam}`;
      await loadBandwidthReport(param);
    };

    if (!canAccessBilling) {
      showUnauthorizedPage();
    } else {
      fetchData();
    }
  }, [duration, showUnauthorizedPage, canAccessBilling, loadBandwidthReport]);

  return (
    <React.Fragment>
      <ContentHeader title={t("billing.page-title")}>
        <MonthPicker
          years={{ min: { year: 2022, month: 1 }, max: { year: maxYear, month: maxMonth } }}
          value={duration}
          onMonthChange={setDuration}
        />
      </ContentHeader>
      <div className={styles["billing-page-container"]} data-test-id="billing-container">
        <iframe title="billing-iframe" data-test-id="billing-iframe" srcDoc={content} />
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state: PartialAppState): BillingStateProps => {
  return {
    canAccessBilling: state.features.canAccessBilling,
    content: state.billing.content,
    title: "billing",
    currentTime: new Date()
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<PartialAppState, void, AnyAction>): BillingDispatchProps => {
  return {
    showUnauthorizedPage: () => dispatch(showUnauthorizedPage()),
    loadBandwidthReport: (duration) => {
      dispatch(fetchBandwidthReport(duration));
      dispatch(
        navigate(
          BILLING_PATH,
          {},
          {
            duration: duration
          }
        )
      );
    }
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps), wrapPage())(Billing);

export { Billing };
